// SPDX-License-Identifier: MIT
//
// Shared connect-surface helpers — the BYOAI provider-connect UI. Relocated
// from `pages/welcome.ts` in SP-2 (welcome went single-column slim and dropped
// its connect surface). These now live on `/connecter` (Phase 4 imports them)
// and are kept here so both the welcome history and the connecter hub can share
// one implementation.
import { renderConnectClaude } from './connect-claude';
import { renderConnectAi } from './connect-ai';
import {
  getUserAiProvider,
  saveUserAiProvider,
  clearUserAiProvider,
} from '../auth/ai-provider';
import { detectPlatform } from '../platform';
import { readDesktopProviderConfig, writeDesktopProviderConfig } from '../platform/provider-store';
import type { SovereignConfig } from '../auth/provider-config';
import { t } from '../i18n/t';
import { escapeHtml } from '../lib/escape';

// "Pourquoi connecter mon Claude ?" — the BYOAI rationale tip card. Moved from
// the welcome newcomer rail; surfaced on /connecter in Phase 4.
export function renderConnectWhyTip(): HTMLElement {
  const el = document.createElement('div');
  el.className = 'rail-card rail-how';
  el.setAttribute('data-component', 'rail-why');
  el.innerHTML = `
    <h4 class="rail-card__h">${escapeHtml(t('dashboard.whyHeading'))}</h4>
    <ol class="rail-how__list">
      <li>${escapeHtml(t('dashboard.why1'))}</li>
      <li>${escapeHtml(t('dashboard.why2'))}</li>
      <li>${escapeHtml(t('dashboard.why3'))}</li>
    </ol>
  `;
  return el;
}

// Static structure of the connect surface: the subscription-first primary
// guide on top, the dev-only API-key form demoted into a collapsed <details>
// below. Synchronous + side-effect-free so it's unit-testable without supabase.
export function renderConnectSurfaceShell(): HTMLElement {
  const surface = document.createElement('div');
  surface.className = 'connect-surface';
  surface.appendChild(renderConnectClaude());

  const dev = document.createElement('details');
  dev.className = 'connect-ai-dev';
  dev.setAttribute('data-component', 'connect-ai-dev');
  const summary = document.createElement('summary');
  summary.className = 'connect-ai-dev__summary';
  summary.textContent = t('connectClaude.devSummary');
  dev.appendChild(summary);
  const body = document.createElement('div');
  body.setAttribute('data-component', 'connect-ai-dev-body');
  dev.appendChild(body);
  surface.appendChild(dev);
  return surface;
}

// Fetch the user's current provider row and render the (unchanged) API-key
// form into the demoted dev <details> body once it lands.
export async function hydrateDevProvider(slot: HTMLElement): Promise<void> {
  const body = slot.querySelector<HTMLElement>('[data-component="connect-ai-dev-body"]');
  if (!body) return;
  const current = await getUserAiProvider();
  // Desktop (Point A): also offer the sovereign-endpoint kind, persisted to the
  // local store. The call leaves the user's machine — Genesis runs no inference.
  const isDesktop = detectPlatform() === 'desktop';
  let currentSovereign: SovereignConfig | null = null;
  if (isDesktop) {
    const cfg = await readDesktopProviderConfig().catch(() => null);
    if (cfg?.kind === 'sovereign' && cfg.endpoint_url && cfg.model) {
      currentSovereign = { endpoint_url: cfg.endpoint_url, model: cfg.model, api_key: cfg.api_key ?? null };
    }
  }
  body.innerHTML = '';
  body.appendChild(renderConnectAi({
    current,
    onSave: async (apiKey) => { await saveUserAiProvider(apiKey); },
    onDisconnect: async () => { await clearUserAiProvider(); },
    ...(isDesktop ? {
      allowSovereign: true,
      currentSovereign,
      onSaveSovereign: async (cfg) => {
        await writeDesktopProviderConfig({ kind: 'sovereign', endpoint_url: cfg.endpoint_url, model: cfg.model, api_key: cfg.api_key ?? null });
      },
    } : {}),
  }));
}
